(() => {
  const euCountries = new Set([
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
  ]);

  const isBusinessDay = (year, month, day) => {
    const weekday = new Date(year, month - 1, day).getDay();
    return weekday !== 0;
  };

  const addBusinessDaysToDate = (year, month, day, days) => {
    let y = year;
    let m = month;
    let d = day;
    let added = 0;
    while (added < days) {
      const next = new Date(y, m - 1, d + 1);
      y = next.getFullYear();
      m = next.getMonth() + 1;
      d = next.getDate();
      if (isBusinessDay(y, m, d)) {
        added += 1;
      }
    }
    return { year: y, month: m, day: d };
  };

  const dateToShopTimezone = (year, month, day, offsetStr) => {
    const m = String(month).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return new Date(`${year}-${m}-${d}T12:00:00${offsetStr}`);
  };

  const getDeliveryRanges = (container) => ({
    DE: {
      min: Number.parseInt(container.dataset.rangeDeMin || "2", 10),
      max: Number.parseInt(container.dataset.rangeDeMax || "4", 10),
    },
    EU: {
      min: Number.parseInt(container.dataset.rangeEuMin || "4", 10),
      max: Number.parseInt(container.dataset.rangeEuMax || "7", 10),
    },
    OTHER: {
      min: Number.parseInt(container.dataset.rangeOtherMin || "7", 10),
      max: Number.parseInt(container.dataset.rangeOtherMax || "14", 10),
    },
  });

  const getRangeForCountry = (deliveryRanges, countryCode) => {
    if (countryCode === "DE") return deliveryRanges.DE;
    if (euCountries.has(countryCode)) return deliveryRanges.EU;
    return deliveryRanges.OTHER;
  };

  const getAvailability = (container) => {
    const product = container.closest(".wt-product");
    const variantOptions = product?.querySelector("variant-options");
    if (variantOptions?.currentVariant) {
      return Boolean(variantOptions.currentVariant.available);
    }
    return container.dataset.inStock === "true";
  };

  const initedContainers = new WeakSet();

  function normalizeDrawerId(container) {
    const inDrawer =
      container.closest(".wt__quick-buy") ||
      container.closest(".wt__quick-buy--wrapper");
    if (inDrawer && container.id === "estimated-delivery") {
      container.id = "estimated-delivery-quick-add";
    }
  }

  function render(container) {
    const locale = document.documentElement.lang || "de-DE";
    const shopTimezone = container.dataset.timezone || "Europe/Berlin";

    const getShopTime = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: shopTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const offsetFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: shopTimezone,
        timeZoneName: "longOffset",
      });

      const parts = formatter.formatToParts(now);
      const partMap = {};
      parts.forEach((part) => {
        if (part.type !== "literal") {
          partMap[part.type] = part.value;
        }
      });

      const offsetParts = offsetFormatter.formatToParts(now);
      const tzPart = offsetParts.find((p) => p.type === "timeZoneName");
      let offsetStr = "+00:00";
      if (tzPart?.value) {
        const m = tzPart.value.match(/(?:UTC|GMT)([+-−])(\d{1,2}):?(\d{2})?/);
        if (m) {
          const sign = m[1] === "−" ? "-" : m[1];
          const hours = m[2].padStart(2, "0");
          const mins = (m[3] || "00").padStart(2, "0");
          offsetStr = `${sign}${hours}:${mins}`;
        }
      }

      const shopYear = Number.parseInt(partMap.year, 10);
      const shopMonth = Number.parseInt(partMap.month, 10);
      const shopDay = Number.parseInt(partMap.day, 10);
      const shopHour = Number.parseInt(partMap.hour, 10);
      return {
        shopYear,
        shopMonth,
        shopDay,
        shopHour,
        offsetStr,
      };
    };

    const rangeElement = container.querySelector("[data-delivery-range]");
    if (!rangeElement) return;

    normalizeDrawerId(container);

    const rangeTemplate =
      container.dataset.rangeTemplate || "%%FROM%% – %%TO%%";
    const unavailableText = container.dataset.unavailable || "";
    const cutoffHour = Number.parseInt(container.dataset.cutoffHour, 10);
    const country = (container.dataset.country || "").toUpperCase();
    const deliveryRanges = getDeliveryRanges(container);

    const getBaseDateParts = (shopYear, shopMonth, shopDay, shopHour) => {
      if (Number.isFinite(cutoffHour) && shopHour >= cutoffHour) {
        const next = new Date(shopYear, shopMonth - 1, shopDay + 1);
        return {
          year: next.getFullYear(),
          month: next.getMonth() + 1,
          day: next.getDate(),
        };
      }
      return { year: shopYear, month: shopMonth, day: shopDay };
    };

    const formatDate = (date) =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        timeZone: shopTimezone,
      }).format(date);

    const formatRange = (from, to) =>
      rangeTemplate.replace("%%FROM%%", from).replace("%%TO%%", to);

    if (!getAvailability(container)) {
      rangeElement.textContent = unavailableText;
      return;
    }

    const { shopYear, shopMonth, shopDay, shopHour, offsetStr } =
      getShopTime();
    const base = getBaseDateParts(shopYear, shopMonth, shopDay, shopHour);
    const range = getRangeForCountry(deliveryRanges, country);
    const start = addBusinessDaysToDate(
      base.year,
      base.month,
      base.day,
      range.min,
    );
    const end = addBusinessDaysToDate(base.year, base.month, base.day, range.max);

    const startDate = dateToShopTimezone(start.year, start.month, start.day, offsetStr);
    const endDate = dateToShopTimezone(end.year, end.month, end.day, offsetStr);

    rangeElement.textContent = formatRange(
      formatDate(startDate),
      formatDate(endDate),
    );
  }

  function initContainers() {
    const containers = document.querySelectorAll(".product__delivery");
    containers.forEach((container) => {
      if (initedContainers.has(container)) return;
      initedContainers.add(container);
      render(container);
    });
  }

  initContainers();

  document.addEventListener("variantChangeEnd", (event) => {
    const variantOptions = event.target;
    if (!variantOptions?.closest?.(".wt-product")) return;
    const product = variantOptions.closest(".wt-product");
    const container = product.querySelector(".product__delivery");
    if (container) render(container);
  });

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.quickBuyDrawerOpen, () => {
      initContainers();
    });
  }
})();
